import { motion } from 'motion/react'
import { useGallery, usePage, useSponsors, useStaff, useValues } from '@/lib/queries'
import { fileUrl } from '@/lib/pb'
import { initials, plainText, titleCase } from '@/lib/utils'
import { Container, MeshTexture, SectionHeading } from '@/components/site/primitives'
import { PageHero } from '@/components/site/page-hero'
import { ValueIcon } from '@/components/site/value-icon'
import { LoadingBlock } from '@/components/ui/feedback'
import { usePageMeta } from '@/components/layout/site-layout'

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.05 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
}

export default function ClubPage() {
  const { data: page, isLoading } = usePage('club')
  const { data: values = [] } = useValues()
  const { data: staff = [] } = useStaff()
  const { data: sponsors = [] } = useSponsors()
  const { data: gallery = [] } = useGallery()

  usePageMeta(page?.title ?? 'The Club', page?.seo_description || plainText(page?.body ?? '', 200))

  if (isLoading)
    return <LoadingBlock className="min-h-[70vh] pt-[calc(var(--header-h)+4rem)]" label="Loading" />

  const tiers = ['principal', 'official', 'community'] as const

  return (
    <>
      <PageHero
        kicker={page?.kicker || 'Who we are'}
        rail="The club"
        title={page?.title || 'The Club'}
        subtitle={page?.subtitle}
        image={fileUrl(page, page?.hero_image, '1600x0')}
      />

      {page?.body ? (
        <section className="border-hairline bg-ink border-b py-14 md:py-20">
          <Container size="narrow">
            <div
              className="prose-viking text-[1.0625rem]"
              dangerouslySetInnerHTML={{ __html: page.body }}
            />
          </Container>
        </section>
      ) : null}

      {values.length > 0 ? (
        <section className="border-hairline bg-ink-sunken relative border-b py-16 md:py-24">
          <MeshTexture opacity={0.05} />
          <Container className="relative">
            <SectionHeading eyebrow="Our values" title="What we stand for" />
            <div className="border-hairline bg-hairline mt-12 grid gap-px border sm:grid-cols-2 lg:grid-cols-3">
              {values.map((value, index) => (
                <motion.div
                  key={value.id}
                  {...reveal}
                  transition={{ ...reveal.transition, delay: index * 0.05 }}
                  className="group bg-ink hover:bg-ink-raised relative p-7 transition-colors"
                >
                  <span className="condensed text-bone-faint/50 absolute top-6 right-6 text-[0.6875rem] tracking-[0.2em]">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <ValueIcon
                    name={value.icon}
                    className="text-blood size-7 transition-transform duration-500 group-hover:scale-110"
                  />
                  <h3 className="condensed text-bone mt-5 text-lg font-bold tracking-[0.04em]">
                    {value.title}
                  </h3>
                  {value.body ? (
                    <p className="text-bone-dim mt-2.5 text-[0.9375rem] leading-relaxed">
                      {value.body}
                    </p>
                  ) : null}
                </motion.div>
              ))}
            </div>
          </Container>
        </section>
      ) : null}

      {staff.length > 0 ? (
        <section className="border-hairline bg-ink border-b py-16 md:py-24">
          <Container>
            <SectionHeading eyebrow="The people" title="Who runs the club" />
            <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {staff.map((member, index) => {
                const photo = fileUrl(member, member.photo, '400x400')
                return (
                  <motion.div
                    key={member.id}
                    {...reveal}
                    transition={{ ...reveal.transition, delay: index * 0.05 }}
                  >
                    {photo ? (
                      <img
                        src={photo}
                        alt={member.name}
                        loading="lazy"
                        className="aspect-4/5 w-full object-cover"
                      />
                    ) : (
                      <div className="from-ink-raised to-ink-sunken flex aspect-4/5 w-full items-center justify-center bg-gradient-to-b">
                        <span className="display text-hairline text-5xl">
                          {initials(member.name)}
                        </span>
                      </div>
                    )}
                    <p className="condensed text-bone mt-4 text-lg font-bold tracking-[0.04em]">
                      {member.name}
                    </p>
                    <p className="eyebrow text-blood mt-1.5">{member.role}</p>
                    {member.bio ? (
                      <div
                        className="prose-viking mt-3 text-[0.875rem]"
                        dangerouslySetInnerHTML={{ __html: member.bio }}
                      />
                    ) : null}
                  </motion.div>
                )
              })}
            </div>
          </Container>
        </section>
      ) : null}

      {gallery.length > 0 ? (
        <section className="border-hairline bg-ink-sunken border-b py-16 md:py-24">
          <Container>
            <SectionHeading eyebrow="The album" title="Around the club" />
            <div className="mt-10 grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
              {gallery.map((item) => (
                <figure
                  key={item.id}
                  className="group bg-ink-raised relative aspect-square overflow-hidden"
                >
                  <img
                    src={fileUrl(item, item.image, '400x400')}
                    alt={item.caption || ''}
                    loading="lazy"
                    className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {item.caption ? (
                    <figcaption className="bg-ink/90 text-bone-dim absolute inset-x-0 bottom-0 translate-y-full p-3 text-xs transition-transform duration-300 group-hover:translate-y-0">
                      {item.caption}
                      {item.credit ? (
                        <span className="text-bone-faint block">© {item.credit}</span>
                      ) : null}
                    </figcaption>
                  ) : null}
                </figure>
              ))}
            </div>
          </Container>
        </section>
      ) : null}

      {sponsors.length > 0 ? (
        <section className="bg-ink py-16 md:py-24">
          <Container>
            <SectionHeading
              eyebrow="Partners"
              title="Who backs us"
              description="Local businesses that keep the pitches booked and the shirts on our backs."
            />
            <div className="mt-10 space-y-10">
              {tiers.map((tier) => {
                const group = sponsors.filter((sponsor) => sponsor.tier === tier)
                if (group.length === 0) return null
                return (
                  <div key={tier}>
                    <p className="eyebrow text-bone-faint mb-4">{titleCase(tier)} partners</p>
                    <div className="border-hairline bg-hairline grid gap-px border sm:grid-cols-2 lg:grid-cols-4">
                      {group.map((sponsor) => {
                        const logo = fileUrl(sponsor, sponsor.logo, '400x400')
                        const inner = (
                          <>
                            {logo ? (
                              <img
                                src={logo}
                                alt={sponsor.name}
                                loading="lazy"
                                className="max-h-14 w-auto object-contain"
                              />
                            ) : (
                              <span className="condensed text-bone-dim group-hover:text-bone text-lg font-semibold tracking-[0.08em] transition-colors">
                                {sponsor.name}
                              </span>
                            )}
                          </>
                        )
                        const className =
                          'group flex min-h-28 items-center justify-center bg-ink p-6 transition-colors hover:bg-ink-raised'
                        return sponsor.url ? (
                          <a
                            key={sponsor.id}
                            href={sponsor.url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className={className}
                          >
                            {inner}
                          </a>
                        ) : (
                          <div key={sponsor.id} className={className}>
                            {inner}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </Container>
        </section>
      ) : null}
    </>
  )
}
